import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAtom } from 'jotai';
import { userAtom } from '../atoms/auth';
import { userLocationAtom } from '../atoms/location';
import { isInHaiti } from '../utils/locationUtils';

interface ReportButtonProps {
  onClick: () => void;
  onAuthRequired: () => void;
  isLoading?: boolean;
}

export default function ReportButton({ onClick, onAuthRequired, isLoading }: ReportButtonProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [userAtomUser] = useAtom(userAtom);
  const [userLocation] = useAtom(userLocationAtom);
  const [isUserInHaiti, setIsUserInHaiti] = useState<boolean | null>(null);

  // Check if user is in Haiti whenever location changes, but only if user is authenticated
  useEffect(() => {
    if (userLocation && userAtomUser) {
      setIsUserInHaiti(isInHaiti(userLocation.lat, userLocation.lng));
    }
  }, [userLocation, userAtomUser]);

  const handleClick = () => {
    if (isLoading) return;
    if (userAtomUser) {
      onClick();
    } else {
      onAuthRequired();
    }
  };

  // Determine if button should be disabled - only if user is authenticated AND not in Haiti
  const isDisabled = isLoading || (userAtomUser && userLocation && isUserInHaiti === false);

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={`rounded-full px-6 py-3 shadow-lg flex items-center space-x-2 transition-colors ${
        userAtomUser
          ? isDisabled 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : 'bg-red-600 text-white hover:bg-red-700'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      title={userAtomUser && userLocation && isUserInHaiti === false ? t('onlyInHaiti') : ''}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">
            {userAtomUser 
              ? (userLocation && isUserInHaiti === false)
                ? t('onlyInHaiti')
                : t('incident.report.button') 
              : t('auth.loginToReport')}
          </span>
        </>
      )}
    </button>
  );
}